// components/ContextMenu.tsx
import React, { useEffect } from 'react';
import { useClipShare } from '../hooks/useClipShare';
import { useAlert } from '../hooks/useAlert';
import { useLanguage } from '../context/LanguageContext';
import { ClipboardService } from '../services/ClipboardService';

export const ContextMenu: React.FC = () => {
  const { contextMenu, hideContextMenu, copyToClipboard } = useClipShare();
  const { showAlert } = useAlert();
  const { t } = useLanguage();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.context-menu')) {
        hideContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible, hideContextMenu]);

  const handleCopyAsPlainText = async () => {
    try {
      const clipboardService = new ClipboardService();
      await clipboardService.copyAsPlainText(contextMenu.content);
      showAlert(t.textCopiedPlain, 'success');
    } catch (err) {
      showAlert(t.failedToCopy, 'error');
    }
    hideContextMenu();
  };

  const handleCopyToClipboard = async () => {
    await copyToClipboard({
      type: contextMenu.type,
      content: contextMenu.content
    });
    hideContextMenu();
  };

  const handleDownloadImage = () => {
    const clipboardService = new ClipboardService();
    clipboardService.downloadImage(contextMenu.content);
    showAlert(t.imageDownloaded, 'success');
    hideContextMenu();
  };

  if (!contextMenu.visible) {
    return null;
  }

  return (
    <div 
      className="context-menu"
      style={{
        left: contextMenu.x,
        top: contextMenu.y,
        position: 'absolute',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '5px 0',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
        zIndex: 1000,
        minWidth: '150px'
      }}
    >
      {contextMenu.type === 'text' ? (
        <>
          <div 
            className="context-menu-item"
            style={{ 
              padding: '10px 15px', 
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={handleCopyAsPlainText}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {t.copyAsPlainText}
          </div>
          <div 
            className="context-menu-item"
            style={{ 
              padding: '10px 15px', 
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={handleCopyToClipboard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {t.copyToClipboard}
          </div>
        </>
      ) : (
        <>
          <div 
            className="context-menu-item"
            style={{ 
              padding: '10px 15px', 
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={handleDownloadImage}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {t.downloadImage}
          </div>
          <div 
            className="context-menu-item"
            style={{ 
              padding: '10px 15px', 
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={handleCopyToClipboard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {t.copyToClipboard}
          </div>
        </>
      )}
    </div>
  );
};
  